import React, { useState, useEffect, useRef } from 'react';
import { Minimize2, Maximize2, Settings, Move } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ColumnDef {
  key: string;
  labelKey?: string;
}

export interface ColumnGroup {
  /** Used for the `${id}:${columnKey}` drag payload namespace. */
  id: string;
  /** i18n key for the group's section label, e.g. 'items.sectionIdentity'. */
  titleKey: string;
  columns: ColumnDef[];
  hiddenColumns: string[];
  onColumnToggle: (columnKey: string) => void;
}

interface ColumnManagerProps {
  hiddenColumns?: string[];
  onColumnToggle?: (columnKey: string) => void;
  onColumnDrop?: (columnKey: string) => void;
  columns?: ColumnDef[];
  /** When present and non-empty, the panel renders in sectioned mode: one titled sub-section per table. */
  groups?: ColumnGroup[];
}

const ColumnManager: React.FC<ColumnManagerProps> = ({
  hiddenColumns = [],
  onColumnToggle,
  onColumnDrop,
  columns,
  groups,
}) => {
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);

  // Position state for dragging the panel
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanelDragging, setIsPanelDragging] = useState(false);
  const [panelDragStart, setPanelDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Load position from localStorage on mount
  useEffect(() => {
    const savedPosition = localStorage.getItem('columnManagerPosition');
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        // Clamp x so ColumnManager never overlaps the sidebar (256px wide on the left)
        const SIDEBAR_WIDTH = 264;
        const elementWidth = 48; // minimized button width
        const defaultRightOffset = 24;
        const padding = 10;
        const minX = -(window.innerWidth - elementWidth - defaultRightOffset - padding - SIDEBAR_WIDTH);
        const safeX = Math.max(parsed.x ?? 0, minX);
        if (safeX !== parsed.x) {
          // Position was in sidebar zone — reset to default and clear storage
          localStorage.removeItem('columnManagerPosition');
          setPosition({ x: 0, y: 0 });
        } else {
          setPosition({ x: safeX, y: parsed.y ?? 0 });
        }
      } catch (e) {
        setPosition({ x: 0, y: 0 });
      }
    }
  }, []);

  // Save position to localStorage whenever it changes
  useEffect(() => {
    if (position.x !== 0 || position.y !== 0) {
      localStorage.setItem('columnManagerPosition', JSON.stringify(position));
    } else {
      localStorage.removeItem('columnManagerPosition');
    }
  }, [position]);

  // Handle panel drag start
  const handlePanelDragStart = (e: React.MouseEvent) => {
    setIsPanelDragging(true);
    setPanelDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  // Reset position to default on double click
  const handleDoubleClick = () => {
    setPosition({ x: 0, y: 0 });
    localStorage.removeItem('columnManagerPosition');
  };

  // Handle panel drag move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isPanelDragging) return;

      const newX = e.clientX - panelDragStart.x;
      const newY = e.clientY - panelDragStart.y;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const elementWidth = containerRef.current?.offsetWidth || 320;
      const elementHeight = containerRef.current?.offsetHeight || 100;
      const defaultBottomOffset = 24;
      const defaultRightOffset = 24;
      const padding = 10;
      const SIDEBAR_WIDTH = 264; // prevent dragging into sidebar zone

      const minX = -(viewportWidth - elementWidth - defaultRightOffset - padding - SIDEBAR_WIDTH);
      const maxX = padding;
      const constrainedX = Math.max(minX, Math.min(newX, maxX));

      const minY = -(viewportHeight - elementHeight - defaultBottomOffset - padding);
      const maxY = padding;
      const constrainedY = Math.max(minY, Math.min(newY, maxY));

      setPosition({ x: constrainedX, y: constrainedY });
    };

    const handleMouseUp = () => {
      setIsPanelDragging(false);
    };

    if (isPanelDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPanelDragging, panelDragStart]);

  const formatColumnName = (key: string, columnsOverride?: ColumnDef[]) => {
    // If columns are provided (flat prop, or a specific group's columns), look up the labelKey for this column key
    const lookupColumns = columnsOverride ?? columns;
    if (lookupColumns) {
      const col = lookupColumns.find((c) => c.key === key);
      if (col?.labelKey) {
        const label = t(col.labelKey);
        if (label && label !== col.labelKey) return label;
      }
    }

    // First try tableHeaders namespace
    let translation = t(`tableHeaders.${key}`);
    if (translation && translation !== `tableHeaders.${key}`) {
      return translation;
    }

    // Then try nestedTable namespace for nested table columns
    translation = t(`nestedTable.${key}`);
    if (translation && translation !== `nestedTable.${key}`) {
      return translation;
    }

    // Then try hr.familyMembers namespace for family member columns
    translation = t(`hr.familyMembers.${key}`);
    if (translation && translation !== `hr.familyMembers.${key}`) {
      return translation;
    }

    // Then try medicalExaminations namespace for medical examination columns
    translation = t(`medicalExaminations.${key}`);
    if (translation && translation !== `medicalExaminations.${key}`) {
      return translation;
    }

    // Then try hr.workBook namespace for work book columns
    translation = t(`hr.workBook.${key}`);
    if (translation && translation !== `hr.workBook.${key}`) {
      return translation;
    }

    // Fallback to formatted key name
    return key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Handle drag over drop zone (for hiding columns)
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  // Handle drop (hide the column)
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const payload = e.dataTransfer.getData("text/plain");
    if (payload) {
      if (groups && groups.length > 0) {
        const separatorIndex = payload.indexOf(":");
        if (separatorIndex !== -1) {
          const groupId = payload.slice(0, separatorIndex);
          const columnKey = payload.slice(separatorIndex + 1);
          const group = groups.find((g) => g.id === groupId);
          if (group) {
            group.onColumnToggle(columnKey);
          } else {
            onColumnDrop?.(payload);
          }
        } else {
          onColumnDrop?.(payload);
        }
      } else {
        onColumnDrop?.(payload);
      }
    }
    setIsDragOver(false);
  };

  // Handle dragging hidden columns back to table
  const handleColumnDragStart = (e: React.DragEvent, columnKey: string) => {
    e.dataTransfer.setData("text/plain", columnKey);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleRestoreColumn = (columnKey: string) => {
    onColumnToggle?.(columnKey);
  };

  const isSectioned = !!groups && groups.length > 0;
  const groupsWithHidden = isSectioned ? groups!.filter((g) => g.hiddenColumns.length > 0) : [];
  const totalHiddenCount = isSectioned
    ? groups!.reduce((sum, g) => sum + g.hiddenColumns.length, 0)
    : hiddenColumns.length;

  if (isMinimized) {
    return (
      <div
        ref={containerRef}
        className="fixed bottom-6 right-6"
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: isPanelDragging ? 'grabbing' : 'grab',
          zIndex: 9000,
        }}
        onMouseDown={handlePanelDragStart}
        onDoubleClick={handleDoubleClick}
        title="Double-click to reset position"
      >
        <button
          onClick={(e) => {
            if (!isPanelDragging) {
              setIsMinimized(false);
            }
            e.stopPropagation();
          }}
          className="text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:opacity-90 relative group"
          style={{ background: `linear-gradient(to right, var(--color-primary), var(--color-secondary))` }}
          title={t("columnManager.openManager")}
        >
          <Settings size={24} />
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            {t("columnManager.dragToMove")}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed bottom-6 right-6"
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        transition: isPanelDragging ? 'none' : 'transform 0.2s ease',
        zIndex: 9000,
      }}
    >
      <div className="bg-white/95 backdrop-blur-sm shadow-2xl rounded-2xl w-80 max-h-96 border border-gray-100 overflow-hidden">
        {/* Header - Draggable */}
        <div
          className="text-white px-4 py-3 flex items-center justify-between group"
          style={{
            background: `linear-gradient(to right, var(--color-primary), var(--color-secondary))`,
            cursor: isPanelDragging ? 'grabbing' : 'grab',
          }}
          onMouseDown={handlePanelDragStart}
          onDoubleClick={handleDoubleClick}
          title="Double-click to reset position"
        >
          <div className="flex items-center gap-2">
            <Move
              size={18}
              className="opacity-60 group-hover:opacity-100 transition-opacity"
            />
            <Settings size={18} />
            <span className="font-semibold">{t("columnManager.title")}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(true);
              }}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              title={t("columnManager.minimize")}
            >
              <Minimize2 size={16} />
            </button>
          </div>
        </div>

        {/* Drop Zone - Drag columns here to hide */}
        <div
          className={`p-4 border-2 border-dashed transition-all duration-200 ${
            isDragOver
              ? 'border-teal-500 bg-teal-50 text-teal-700'
              : 'border-gray-300 bg-gray-50 text-gray-600'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <div className="text-2xl mb-2">📋</div>
            <p className="text-sm font-medium">
              {isDragOver ? t("columnManager.dropZoneActive") : t("columnManager.dropZoneText")}
            </p>
          </div>
        </div>

        {/* Hidden Columns List */}
        <div className="p-4 max-h-48 overflow-y-auto">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span>{t("columnManager.hiddenColumns")}</span>
            {totalHiddenCount > 0 && (
              <span className="bg-teal-100 text-teal-800 text-xs px-2 py-1 rounded-full">
                {totalHiddenCount}
              </span>
            )}
          </h4>

          {isSectioned ? (
            groupsWithHidden.length === 0 ? (
              <p className="text-gray-500 text-sm italic">{t("columnManager.noHiddenColumns")}</p>
            ) : (
              <div className="space-y-4">
                {groupsWithHidden.map((group) => (
                  <div key={group.id}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {t(group.titleKey)}
                    </p>
                    <div className="space-y-2">
                      {group.hiddenColumns.map((columnKey) => (
                        <div
                          key={columnKey}
                          draggable
                          onDragStart={(e) => handleColumnDragStart(e, columnKey)}
                          className="flex items-center justify-between p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-move group"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 cursor-grab active:cursor-grabbing">⋮⋮</span>
                            <span className="text-sm font-medium text-gray-700">
                              {formatColumnName(columnKey, group.columns)}
                            </span>
                          </div>
                          <button
                            onClick={() => group.onColumnToggle(columnKey)}
                            className="text-teal-600 hover:text-teal-800 opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t("columnManager.restoreColumn")}
                          >
                            <Maximize2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : hiddenColumns.length === 0 ? (
            <p className="text-gray-500 text-sm italic">{t("columnManager.noHiddenColumns")}</p>
          ) : (
            <div className="space-y-2">
              {hiddenColumns.map((columnKey) => (
                <div
                  key={columnKey}
                  draggable
                  onDragStart={(e) => handleColumnDragStart(e, columnKey)}
                  className="flex items-center justify-between p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-move group"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 cursor-grab active:cursor-grabbing">⋮⋮</span>
                    <span className="text-sm font-medium text-gray-700">
                      {formatColumnName(columnKey)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRestoreColumn(columnKey)}
                    className="text-teal-600 hover:text-teal-800 opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t("columnManager.restoreColumn")}
                  >
                    <Maximize2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            {t("columnManager.footerText")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ColumnManager;
